import React, { useMemo } from 'react';

import { CustomUTMOverview } from '../../../../utils/models';
import { getCustomUTMTableColumns } from '../../../../utils/data';
import { formatTime, getChunkedData } from '../../../../utils/utilities';
import { ALPHABET, APP_DATE_FORMAT } from '../../../../utils/constants';

import CustomSection from '../../custom-general/custom-section';
import GridItem from '../../../pages/reports/general/gridItem';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';
import Conditional from '../../../common/conditional';

interface CustomUTMeasurementsProps extends CustomUTMOverview {
	includeSize: boolean;
	sectionTitle?: string;
	sectionSubtitle: string;
	readingStartColumn: string;
	readingEndColumn: string;
}

const CustomUTMeasurementsOverview: React.FC<CustomUTMeasurementsProps> = (props) => {
	const dataColumns = useMemo(() => {
		const CUT_OFF_LETTER = 'H';

		const actualEndIndex = ALPHABET.findIndex((letter) => letter === props.readingEndColumn);
		const projectedEndIndex = ALPHABET.findIndex((letter) => letter === CUT_OFF_LETTER);

		const allColumns = getCustomUTMTableColumns(props.readingStartColumn, props.readingEndColumn, props.includeSize, props.data, props.corrosionAllowance);
		if (actualEndIndex <= projectedEndIndex) return [allColumns];

		const readingStartIndex = allColumns.findIndex((col) => col.path === props.readingStartColumn);
		const readingEndIndex = allColumns.findIndex((col) => col.path === props.readingEndColumn) + 1;
		const readingColumns = allColumns.slice(readingStartIndex, readingEndIndex);

		const chunkedData = getChunkedData(readingColumns, projectedEndIndex).map((data) => ([...allColumns.slice(0, readingStartIndex), ...data, ...allColumns.slice(readingEndIndex)]));
		return chunkedData;
	}, [props]);

	return (
		<>
			{dataColumns.map((column, index) => (
				<CustomSection key={index} sectionTitle={props.sectionTitle ?? 'Ultrasonic Test Report'} sectionSubtitle={props.sectionSubtitle}>
					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className="col">
								<GridItem title="NDE Technique:" value={props.ndeTechnique} />
							</div>
							<div className="col">
								<GridItem title="Equipment:" value={props.equipment} />
							</div>
						</div>
					</div>
		
					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className="col">
								<GridItem title="Calibration Due Date:" value={formatTime(props.calibrationDueDate, APP_DATE_FORMAT)} />
							</div>
							<div className="col">
								<GridItem title="Serial No:" value={props.serialNo} />
							</div>
						</div>
					</div>
		
					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className="col">
								<GridItem title={`Corrosion Allowance`} value={`${props.corrosionAllowance}${props.unit}`} />
							</div>
		
							<div className="col">
								<GridItem title="Date of UTM:" value={props.dateOfUTM ? formatTime(props.dateOfUTM, APP_DATE_FORMAT) : ''} />	
							</div>
						</div>
					</div>
		
					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className="col">
								<GridItem title="Reference Std:" value={props.refStd} />
								<GridItem title="Probe:" value={props.probe} />
							</div>
		
							<div className="col">
								<GridItem title="Material" value={props.material} />
								<GridItem title="Couplant:" value={props.couplant} />	
							</div>
						</div>
					</div>
		
					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className="col">
								<GridItem title="Calibration Blk:" value={props.calibrationBlk} />
								<GridItem title="Amplitude Linearity Check:" value={props.amplitudeLinearityCheck} />
							</div>
							<div className="col">
								<GridItem title="Scanning Level:" value={props.scanningLevel} />
								<GridItem title="Screen Height Linearity Check:" value={props.screenHeightLinearityCheck} />
							</div>
						</div>
					</div>
					
					<div className="container-fluid overflow-hidden">
						<div className="row">
							<div className="col">
								<GridItem title="Technique Type:" value={props.techniqueType} />
							</div>
							<div className="col">
								<GridItem title="Velocity (m/s):" value={props.velocity} />
							</div>
						</div>
					</div>
		
					<div className="p-1 table-responsive">
						<Conditional isVisible={index === dataColumns.length - 1}>
							<PDFTablePage data={props.data} columns={column} />
						</Conditional>
						
						<Conditional isVisible={index !== dataColumns.length - 1}>
							<PDFTablePage data={props.data} columns={column.filter((col) => !['minimum', 'maximum', 'average', 'comments'].includes(col.path))} />
						</Conditional>
					</div>
		
					<Conditional isVisible={index === dataColumns.length - 1}>
						<div className="container-fluid overflow-hidden">
							<GridItem title="Remarks:" value={props.remarks} />
						</div>
					</Conditional>
					
					<Conditional isVisible={props.summary.length > 0}>
						<div className="container-fluid overflow-hidden">
							<GridItem title="Summary:" value={props.summary} />
						</div>
					</Conditional>
				</CustomSection>
			))}
		</>
	);
};

export default CustomUTMeasurementsOverview;
